import sanitizeHtml from "sanitize-html"

const isObject = (obj) => obj !== null && typeof obj === "object"

// Stolen directly from vue-i18n sourcecode so that I can remove the flow typings
// https://github.com/kazupon/vue-i18n/blob/v8.x/src/format.js
// --------------------------- REFERENCE CODE START ---------------------------
class BaseFormatter {
  constructor() {
    this._caches = Object.create(null)
  }

  interpolate(message, values) {
    if (!values) {
      return [message]
    }
    let tokens = this._caches[message]
    if (!tokens) {
      tokens = parse(message)
      this._caches[message] = tokens
    }
    return compile(tokens, values)
  }
}

const RE_TOKEN_LIST_VALUE = /^(?:\d)+/
const RE_TOKEN_NAMED_VALUE = /^(?:\w)+/

function parse(format) {
  const tokens = []
  let position = 0

  let text = ""
  while (position < format.length) {
    let char = format[position++]
    if (char === "{") {
      if (text) {
        tokens.push({ type: "text", value: text })
      }

      text = ""
      let sub = ""
      char = format[position++]
      while (char !== undefined && char !== "}") {
        sub += char
        char = format[position++]
      }
      const isClosed = char === "}"

      const type = RE_TOKEN_LIST_VALUE.test(sub)
        ? "list"
        : isClosed && RE_TOKEN_NAMED_VALUE.test(sub)
        ? "named"
        : "unknown"
      tokens.push({ value: sub, type })
    } else if (char === "%") {
      // when found rails i18n syntax, skip text capture
      if (format[position] !== "{") {
        text += char
      }
    } else {
      text += char
    }
  }

  text && tokens.push({ type: "text", value: text })

  return tokens
}

function compile(tokens, values) {
  const compiled = []
  let index = 0

  const mode = Array.isArray(values) ? "list" : isObject(values) ? "named" : "unknown"
  if (mode === "unknown") {
    return compiled
  }

  while (index < tokens.length) {
    const token = tokens[index]
    switch (token.type) {
      case "text":
        compiled.push(token.value)
        break
      case "list":
        compiled.push(values[parseInt(token.value, 10)])
        break
      case "named":
        if (mode === "named") {
          compiled.push(values[token.value])
        } else {
          // eslint-disable-next-line no-undef
          if (process.env.NODE_ENV !== "production") {
            console.warn(`Type of token '${token.type}' and format of value '${mode}' don't match!`)
          }
        }
        break
      case "unknown":
        // eslint-disable-next-line no-undef
        if (process.env.NODE_ENV !== "production") {
          console.warn("Detect 'unknown' type of token!")
        }
        break
    }
    index++
  }

  return compiled
}

// --------------------------- REFERENCE CODE END ---------------------------

const sanitiseValues = (values) => {
  if (Array.isArray(values)) {
    return values.map((value) => sanitizeHtml(value))
  }

  if (isObject(values)) {
    const sanitizedValues = {}
    Object.keys(values).forEach((key) => {
      sanitizedValues[key] = sanitizeHtml(values[key])
    })
    return sanitizedValues
  }

  if (typeof values === "string") {
    return sanitizeHtml(values)
  }

  return values
}

// From: https://kazupon.github.io/vue-i18n/guide/formatting.html#custom-formatting
export class SanitiserFormatter extends BaseFormatter {
  constructor(options) {
    super(options)
  }

  interpolate(message, values) {
    if (!values) {
      return [message]
    }
    let tokens = this._caches[message]
    if (!tokens) {
      tokens = parse(message)
      this._caches[message] = tokens
    }
    const sanitizedValues = sanitiseValues(values)

    return compile(tokens, sanitizedValues)
  }
}
